import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    ArrowBack,
    Launch,
    ContentCopy,
    Delete,
    CalendarToday,
    Timer,
    Person,
} from '@mui/icons-material';
import {copyToClipboard, formatRelativeTime2} from '@/utils/helpers';

import { toast } from 'react-toastify';
import { meetingService } from '@services/api';
import MeetingSummary from "../Summary/MeetingSummary"; // Adjust import as needed
export const STATUS_COLORS = {
    SCHEDULED: 'primary',
    STARTED: 'success',
    ENDED: 'default',
    CANCELLED: 'error'
};
const MeetingDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [meeting, setMeeting] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMeeting = async () => {
            try {
                setLoading(true);
                const response = await meetingService.getMeetingById(id);
                setMeeting(response.data);
            } catch (err) {
                console.error('Error fetching meeting:', err);
                setError('Failed to load meeting details');
                toast.error('Failed to load meeting details');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchMeeting();
        }
    }, [id]);

    const handleCopyLink = async () => {
        if (meeting?.joinUrl) {
            const success = await copyToClipboard(meeting.joinUrl);
            if (success) {
                toast.success('Meeting link copied!');
            }
        }
    };

    const handleJoinMeeting = () => {
        if (meeting?.joinUrl) {
            window.open(meeting.joinUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const handleDelete = async () => {
        if (window.confirm(`Delete meeting "${meeting?.topic}"?`)) {
            try {
                await meetingService.deleteMeeting(id);
                toast.success('Meeting deleted');
                navigate('/interviews');
            } catch (err) {
                toast.error('Failed to delete meeting');
            }
        }
    };
    const [endingMeeting, setEndingMeeting] = useState(false); // new state
    const handleEndMeeting = async () => {
        if (window.confirm('Mark this meeting as ended? This will trigger AI summary generation.')) {
            try {
                setEndingMeeting(true); // start loading
                await meetingService.updateMeetingStatus(meeting.id, 'ENDED');
                toast.success('Meeting ended. Summary will be generated shortly...');

                // Refetch updated meeting details
                const response = await meetingService.getMeetingById(meeting.id);
                setMeeting(response.data);
            } catch (error) {
                toast.error('Failed to end meeting');
            } finally {
                setEndingMeeting(false); // stop loading
            }
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error || !meeting) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="error" gutterBottom>
                        {error || 'Meeting not found'}
                    </Typography>
                    <Button startIcon={<ArrowBack />} onClick={() => navigate('/interviews')}>
                        Back to Meetings
                    </Button>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="fullWidth" sx={{ py: 4 }}>
            <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/interviews')}
                sx={{ mb: 3 }}
            >
                Back to Meetings
            </Button>

            <Paper sx={{ p: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                    <Box>
                        <Typography variant="h5" gutterBottom fontWeight="600">
                            {meeting.topic}
                        </Typography>
                        <Chip
                            label={meeting.status}
                            color={STATUS_COLORS[meeting.status]}
                            size="small"
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {meeting.status === 'SCHEDULED' && (
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={handleEndMeeting}
                                disabled={endingMeeting} // block multiple clicks
                                startIcon={endingMeeting ? <CircularProgress size={20} /> : null} // show spinner
                            >
                                {endingMeeting ? 'Ending...' : 'End Meeting'}
                            </Button>
                        )}

                        <Button
                            variant="contained"
                            startIcon={<Launch />}
                            onClick={() => window.open(meeting.joinUrl, '_blank')}
                            disabled={meeting.status === 'CANCELLED'}
                        >
                            Join Meeting
                        </Button>
                    </Box>
                </Box>

                {meeting.agenda && (
                    <>
                        <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                            Agenda
                        </Typography>
                        <Typography color="text.secondary" paragraph>
                            {meeting.agenda}
                        </Typography>
                    </>
                )}

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: 'grid', gap: 2 }}>
                    <Box display="flex" alignItems="center">
                        <CalendarToday sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Start Time
                            </Typography>
                            <Typography>{formatRelativeTime2(meeting.startTime)}</Typography>
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center">
                        <Timer sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Duration
                            </Typography>
                            <Typography>{meeting.duration} minutes</Typography>
                        </Box>
                    </Box>

                    {meeting.hostEmail && (
                        <Box display="flex" alignItems="center">
                            <Person sx={{ mr: 2, color: 'text.secondary' }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Host
                                </Typography>
                                <Typography>{meeting.hostEmail}</Typography>
                            </Box>
                        </Box>
                    )}
                </Box>

                {meeting.password && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="body2">
                            <strong>Password:</strong> {meeting.password}
                        </Typography>
                    </Box>
                )}

                <Divider sx={{ my: 3 }} />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" gap={1}>
                        <Tooltip title="Copy meeting link">
                            <IconButton color="primary" onClick={handleCopyLink}>
                                <ContentCopy />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete meeting">
                            <IconButton color="error" onClick={handleDelete}>
                                <Delete />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<Launch />}
                        onClick={handleJoinMeeting}
                        disabled={meeting.status === 'CANCELLED'}
                    >
                        Join Meeting
                    </Button>
                </Box>
            </Paper>
            {meeting.status === 'ENDED' && (
                <Box sx={{ mt: 3 }}>
                    <MeetingSummary meetingId={meeting.id} />
                </Box>
            )}
        </Container>
    );
};

export default MeetingDetail;